/* Sweat Entertainment — "bend + RGB split" image layer.
   A port of the sweatstrategies.com artist-card effect (three.js chromatic
   aberration that follows the cursor, plus a scroll-scrubbed tilt) to raw
   WebGL with no dependencies. One fixed canvas draws every visible
   .sw-card__media image as a subdivided quad:
     - vertex: the sheet bows and tilts with smoothed scroll velocity
     - fragment: red, green and blue sample at staggered offsets around the
       cursor (desktop) or along the scroll direction (touch)
   The <img> stays in the DOM for layout, no-JS, print and search; it is only
   faded out once its texture is ready. Anything failing (no WebGL, tainted
   texture, reduced motion) leaves the plain images untouched. */
(function () {
  'use strict';

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var imgs = Array.prototype.slice.call(document.querySelectorAll('.sw-card__media img'));
  if (!imgs.length) return;

  var canvas = document.createElement('canvas');
  canvas.className = 'sw-bend-canvas';
  canvas.setAttribute('aria-hidden', 'true');
  var gl = canvas.getContext('webgl', { alpha: true, antialias: false, premultipliedAlpha: true });
  if (!gl) return;
  document.body.appendChild(canvas);

  var VERT = [
    'precision highp float;',
    'attribute vec2 aPos;',            // 0..1 across the quad
    'uniform vec4 uRect;',             // x, y, w, h in CSS px (top-left origin)
    'uniform vec2 uView;',             // viewport w, h
    'uniform float uBend;',            // smoothed scroll velocity, roughly -1..1
    'uniform float uTilt;',            // -1..1 by position in viewport
    'varying vec2 vUv;',
    'void main() {',
    '  vUv = aPos;',
    '  vec2 p = aPos;',
    '  float bow = sin(p.y * 3.14159) * uBend * 0.26;',       // sheet bows sideways with velocity
    '  float lean = (p.y - 0.5) * uTilt * 0.16;',             // top edge leads, bottom trails
    '  p.x += bow + lean;',
    '  p.y += (p.x - 0.5) * uBend * 0.09;',                   // slight skew
    '  vec2 px = uRect.xy + p * uRect.zw;',
    '  vec2 clip = vec2(px.x / uView.x * 2.0 - 1.0, 1.0 - px.y / uView.y * 2.0);',
    '  gl_Position = vec4(clip, 0.0, 1.0);',
    '}'
  ].join('\n');

  var FRAG = [
    'precision highp float;',
    'uniform sampler2D uTex;',
    'uniform vec2 uCover;',            // uv scale for object-fit: cover
    'uniform vec2 uMouse;',            // cursor in quad uv, or (-10,-10)
    'uniform float uAspect;',          // quad h / w
    'uniform float uBend;',
    'uniform float uHover;',           // 0..1, eases in when the cursor is over the card
    'varying vec2 vUv;',
    'float circle(vec2 uv, vec2 c, float r, float border) {',
    '  uv -= c; uv.y *= uAspect;',
    '  float d = sqrt(dot(uv, uv));',
    '  return smoothstep(r + border, r - border, d);',
    '}',
    'void main() {',
    '  vec2 uv = (vUv - 0.5) * uCover + 0.5;',
    '  float c = circle(vUv, uMouse, 0.0, 0.65) * uHover;',   // same falloff as the Strategies pass
    '  float v = uBend * 0.6;',                                // scroll-driven split on every device
    '  vec2 dir = vec2(1.0, 1.0);',
    '  float amt = c * 0.05 + abs(v) * 0.035;',
    '  if (abs(v) > c * 0.05) dir = vec2(0.0, sign(v));',
    '  vec2 o = dir * amt;',
    '  vec2 r_uv = uv + o * 0.50;',
    '  vec2 g_uv = uv + o * 1.025;',
    '  vec2 b_uv = uv + o * 1.575;',
    '  float r = texture2D(uTex, clamp(r_uv, 0.001, 0.999)).r;',
    '  float g = texture2D(uTex, clamp(g_uv, 0.001, 0.999)).g;',
    '  float b = texture2D(uTex, clamp(b_uv, 0.001, 0.999)).b;',
    '  gl_FragColor = vec4(r, g, b, 1.0);',
    '}'
  ].join('\n');

  function shader(type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { return null; }
    return s;
  }
  var vs = shader(gl.VERTEX_SHADER, VERT);
  var fs = shader(gl.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) return;
  var prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
  gl.useProgram(prog);

  /* subdivided quad, 1 x 24 strips, as a triangle strip per strip */
  var SEG = 24;
  var verts = [];
  for (var i = 0; i < SEG; i++) {
    var y0 = i / SEG, y1 = (i + 1) / SEG;
    verts.push(0, y0, 1, y0, 0, y1, 1, y0, 1, y1, 0, y1);
  }
  var buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verts), gl.STATIC_DRAW);
  var aPos = gl.getAttribLocation(prog, 'aPos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  var U = {};
  ['uRect', 'uView', 'uBend', 'uTilt', 'uTex', 'uCover', 'uMouse', 'uAspect', 'uHover'].forEach(function (n) {
    U[n] = gl.getUniformLocation(prog, n);
  });

  var DPR = Math.min(window.devicePixelRatio || 1, 1.5);
  var vw = 0, vh = 0;
  function resize() {
    vw = window.innerWidth; vh = window.innerHeight;
    canvas.width = Math.round(vw * DPR);
    canvas.height = Math.round(vh * DPR);
    canvas.style.width = vw + 'px';
    canvas.style.height = vh + 'px';
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(U.uView, vw, vh);
  }
  window.addEventListener('resize', resize, { passive: true });
  resize();

  /* one entry per image: texture, natural aspect, hover state */
  var entries = [];
  imgs.forEach(function (img) {
    var e = { img: img, holder: img.parentNode, tex: null, w: 1, h: 1, hover: 0, mouse: [-10, -10], over: false };
    var src = img.currentSrc || img.src;
    if (!src) return;
    var loader = new Image();
    loader.crossOrigin = 'anonymous';
    loader.onload = function () {
      try {
        var t = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);   /* quad uv has y=0 at the top, same as the image */
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, loader);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        e.tex = t; e.w = loader.naturalWidth; e.h = loader.naturalHeight;
        e.holder.classList.add('sw-bend-ready');
      } catch (err) { /* tainted or lost context: keep the plain image */ }
    };
    loader.src = src;
    e.holder.addEventListener('mousemove', function (ev) {
      var r = e.holder.getBoundingClientRect();
      e.mouse = [(ev.clientX - r.left) / r.width, (ev.clientY - r.top) / r.height];
      e.over = true;
    });
    e.holder.addEventListener('mouseleave', function () { e.over = false; });
    entries.push(e);
  });

  /* scroll velocity, smoothed */
  var lastY = window.pageYOffset, vel = 0, bend = 0, idleFrames = 0;
  window.addEventListener('scroll', function () {
    var y = window.pageYOffset;
    vel += (y - lastY) * 0.0038;
    lastY = y;
    idleFrames = 0;
  }, { passive: true });

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

  function frame() {
    requestAnimationFrame(frame);
    vel *= 0.9;
    if (vel > 1) vel = 1; if (vel < -1) vel = -1;
    bend += (vel - bend) * 0.14;
    idleFrames++;
    var anyHover = false;
    for (var k = 0; k < entries.length; k++) if (entries[k].over) anyHover = true;
    /* nothing moving and nobody hovering: hold the last frame, spare the GPU */
    if (idleFrames > 90 && Math.abs(bend) < 0.001 && !anyHover) {
      var still = true;
      for (var q = 0; q < entries.length; q++) if (entries[q].hover > 0.002) still = false;
      if (still) return;
    }
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      if (!e.tex) continue;
      var r = e.holder.getBoundingClientRect();
      if (r.bottom < -80 || r.top > vh + 80 || r.width < 2) continue;
      e.hover += ((e.over ? 1 : 0) - e.hover) * 0.12;
      /* cover-fit: scale uv so the image fills the box */
      var boxA = r.width / r.height, imgA = e.w / e.h;
      var cx = 1, cy = 1;
      if (imgA > boxA) cx = boxA / imgA; else cy = imgA / boxA;
      /* tilt by where the card sits: above centre leans one way, below the other */
      var centre = (r.top + r.height / 2) / vh - 0.5;
      var tilt = Math.max(-1, Math.min(1, centre * 1.6)) * (0.3 + Math.abs(bend) * 1.5);
      gl.bindTexture(gl.TEXTURE_2D, e.tex);
      gl.uniform1i(U.uTex, 0);
      gl.uniform4f(U.uRect, r.left, r.top, r.width, r.height);
      gl.uniform2f(U.uCover, cx, cy);
      gl.uniform1f(U.uBend, bend);
      gl.uniform1f(U.uTilt, tilt);
      gl.uniform1f(U.uAspect, r.height / r.width);
      gl.uniform1f(U.uHover, e.hover);
      gl.uniform2f(U.uMouse, e.mouse[0], e.mouse[1]);
      gl.drawArrays(gl.TRIANGLES, 0, SEG * 6);
    }
  }
  requestAnimationFrame(frame);

  canvas.addEventListener('webglcontextlost', function () {
    entries.forEach(function (e) { e.holder.classList.remove('sw-bend-ready'); });
    canvas.remove();
  });
})();
